import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, Image, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import error404 from 'assets/404_warning_icon.png';
import NoOutputIndicatorIcon from 'components/Icons/NoOutputIndicator';
import { useAuthContext } from 'context/authContext';
import { NewDimItemData } from 'models/response/blockOutput.interface';
import { STATUS } from 'projectConstants';
import {
  chartOutputStatus,
  getActiveTabIndex,
  getBlockDetails,
  getChartLabels,
  getChartType,
  setActiveTabIndexVersion2,
  setChartType,
} from 'redux/PlanPageSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import Chart from '../../Plan/Chart';
import {
  selectActiveChartSeries,
  selectDefaultTabIndex,
  selectSafeActiveIndex,
} from '../planPageRevampSelectors';
import {
  PLAN_CHART_VIEW_HEIGHT,
  PLAN_HEADER_HORIZONTAL_PADDING,
  PLAN_TEXT,
} from '../planPageRevampUi';
import { buildChartImageFileName, downloadChartImage } from '../planChartDownload';

import '../planChartSlider.css';
import PlanChartSkeleton from './PlanChartSkeleton';
import PlanChartToolbar from './PlanChartToolbar';

import type { ChartTypeRegistry } from 'chart.js';
import type { ChartJSOrUndefined } from 'react-chartjs-2/dist/types';

interface PlanChartViewProperties {
  chartType: string;
  chartData: NewDimItemData[];
  chartLabels: string[];
  tabData: unknown;
  activeTabIndex: number;
  onChartTypeChange: (value: string) => void;
  onDownload: () => void;
  isExporting: boolean;
  surfaceColor: string;
  fillHeight?: boolean;
  chartAreaHeight?: string;
  chartExportReference?: React.MutableRefObject<ChartJSOrUndefined<keyof ChartTypeRegistry> | null>;
}

const PlanChartView = memo(
  ({
    chartType,
    chartData,
    chartLabels,
    tabData,
    activeTabIndex,
    onChartTypeChange,
    onDownload,
    isExporting,
    surfaceColor,
    fillHeight = false,
    chartAreaHeight,
    chartExportReference,
  }: PlanChartViewProperties) => {
    const resolvedChartHeight = chartAreaHeight ?? PLAN_CHART_VIEW_HEIGHT;

    return (
      <Box
        display='flex'
        flexDirection='column'
        h={fillHeight ? '100%' : 'auto'}
        flex={fillHeight ? '1' : undefined}
        flexShrink={fillHeight ? undefined : 0}
        minH={fillHeight ? 0 : undefined}
        w='100%'
        bg={surfaceColor}
        overflow={fillHeight ? 'hidden' : 'visible'}
      >
        <PlanChartToolbar
          viewMode={chartType}
          onChange={onChartTypeChange}
          onDownload={onDownload}
          isExporting={isExporting}
        />

        <Box
          flex={fillHeight ? '1' : undefined}
          minH={fillHeight ? 0 : undefined}
          flexShrink={0}
          display='flex'
          flexDirection='column'
          w='100%'
          px={PLAN_HEADER_HORIZONTAL_PADDING}
          overflow={fillHeight ? 'hidden' : 'visible'}
        >
          <Chart
            hideToolbar
            variant='revamp'
            chartData={chartData}
            chartLabels={chartLabels}
            tabData={tabData}
            activeTabIndex={activeTabIndex}
            exportRef={chartExportReference}
            plotHeight={resolvedChartHeight}
            fillPlotHeight={fillHeight}
          />
        </Box>
      </Box>
    );
  },
);

PlanChartView.displayName = 'PlanChartView';

const toChartData = (series: any): NewDimItemData[] =>
  series.dim_item_data ?? [{ value: series.total_values, name: 'Total' }];

interface PlanChartPanelProperties {
  fillHeight?: boolean;
  chartAreaHeight?: string;
  onToggleAuthModal: () => void;
}

const PlanChartPanel = ({
  fillHeight = false,
  chartAreaHeight,
  onToggleAuthModal,
}: PlanChartPanelProperties) => {
  const { user } = useAuthContext();
  const dispatch: AppDispatch = useDispatch();
  const chartExportReference = useRef<ChartJSOrUndefined<keyof ChartTypeRegistry> | null>(null);
  const activeSeries = useSelector(selectActiveChartSeries);
  const safeActiveIndex = useSelector(selectSafeActiveIndex);
  const defaultTabIndex = useSelector(selectDefaultTabIndex);
  const activeTabIndex = useSelector(getActiveTabIndex);
  const tabsDataLength = useSelector((state: any) => state.planPageSlice.chartOutput?.length ?? 0);
  const chartLabels = useSelector(getChartLabels);
  const chartStatus = useSelector(chartOutputStatus);
  const chartType = useSelector(getChartType);
  const blockDetails = useSelector(getBlockDetails);
  const themeColors = useSelector(getPlanPageThemeColors);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (activeTabIndex === -1 && tabsDataLength > 0) {
      dispatch(setActiveTabIndexVersion2(defaultTabIndex));
    }
  }, [activeTabIndex, defaultTabIndex, dispatch, tabsDataLength]);

  const chartData = useMemo(() => (activeSeries ? toChartData(activeSeries) : []), [activeSeries]);

  const onChartTypeChange = useCallback(
    (value: string) => {
      dispatch(setChartType(value));
    },
    [dispatch],
  );

  const onDownload = useCallback(async () => {
    if (user?.is_anonymous) {
      onToggleAuthModal();
      return;
    }

    setIsExporting(true);
    try {
      const fileName = buildChartImageFileName(blockDetails?.name);
      downloadChartImage(chartExportReference.current, fileName);
    } finally {
      setIsExporting(false);
    }
  }, [blockDetails?.name, onToggleAuthModal, user?.is_anonymous]);

  const hasCachedChart = tabsDataLength > 0;

  if (
    (chartStatus === STATUS.LOADING && !hasCachedChart) ||
    (chartStatus === STATUS.IDLE && tabsDataLength === 0)
  ) {
    return (
      <PlanChartSkeleton
        fillHeight={fillHeight}
        chartAreaHeight={chartAreaHeight}
        surfaceColor={themeColors.surface}
        horizontalPadding={PLAN_HEADER_HORIZONTAL_PADDING}
      />
    );
  }

  if (
    (tabsDataLength === 0 && chartStatus === STATUS.FAILED) ||
    (tabsDataLength === 0 && chartStatus === STATUS.SUCCESS)
  ) {
    return (
      <Box w='100%' py={8} display='flex' justifyContent='center' alignItems='center'>
        {chartStatus === STATUS.SUCCESS && <NoOutputIndicatorIcon />}
        {chartStatus === STATUS.FAILED && (
          <Flex direction='column' gap={2} alignItems='center'>
            <Image src={error404} alt='error icon' boxSize={40} />
            <Text {...PLAN_TEXT.body} color={themeColors.textSecondary}>
              Something went wrong, Please contact support@blox.so
            </Text>
          </Flex>
        )}
      </Box>
    );
  }

  if (!activeSeries || !chartLabels?.length) {
    return (
      <PlanChartSkeleton
        fillHeight={fillHeight}
        chartAreaHeight={chartAreaHeight}
        surfaceColor={themeColors.surface}
        horizontalPadding={PLAN_HEADER_HORIZONTAL_PADDING}
      />
    );
  }

  return (
    <PlanChartView
      chartType={chartType}
      chartData={chartData}
      chartLabels={chartLabels}
      tabData={activeSeries}
      activeTabIndex={safeActiveIndex}
      onChartTypeChange={onChartTypeChange}
      onDownload={onDownload}
      isExporting={isExporting}
      surfaceColor={themeColors.surface}
      fillHeight={fillHeight}
      chartAreaHeight={chartAreaHeight}
      chartExportReference={chartExportReference}
    />
  );
};

export default memo(PlanChartPanel);
